import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

import { PageMetaData } from '@/components/atomic/PageMetaData'
import { Header } from '@/components/styleguide/Header'
import { SearchContentHeader } from '@/components/styleguide/SearchContentHeader'
import { MakeModelLanding } from '@/components/views/MakeModelLanding'
import { SearchResultsAndFilters } from '@/components/views/SearchResultsAndFilters'
import { constants, FILTER_VEHICLE_TYPES_CARS } from '@/lib/constants'
import { FILTERS } from '@/lib/filters'
import { getPageContentItemService } from '@/lib/services/pageService'
import { getRangesForManufacturersService } from '@/lib/services/searchService'
import { getListOfAllSuppliersService } from '@/lib/services/supplierService'
import { INITIAL_STATE, landedOnCarSearchPageWithPath } from '@/store/reducers/search'

const CarRangePage = ({ isAlt, pageContent, range, faqList, supplierCount }) => {
  const router = useRouter()
  const dispatch = useDispatch()
  const [showPageContent, setShowPageContent] = useState(true)
  const [isVans] = useState(false)
  const [status, setStatus] = useState(constants.componentStatus.LOADING)

  useEffect(() => {
    if (router.query.page) {
      setShowPageContent(false)
    }
  }, [router.query.page])

  useEffect(() => {
    setStatus(constants.componentStatus.LOADING)
    dispatch(landedOnCarSearchPageWithPath(router.asPath))
  }, [router.asPath, dispatch])

  return (
    <>
      <PageMetaData
        title={pageContent.pageTitle}
        pageTitle={pageContent.pageTitle}
        description={pageContent.metaDescription}
        pageUrl={router.asPath.split('?')[0]}
        canonical={`${process.env.NEXT_PUBLIC_URL}${router.asPath.split('?')[0]}`}
        image={pageContent.image}
        noIndex={isAlt ? true : router.asPath.includes('page=')}
      />
      <Header
        width={isAlt ? 'make-model' : 'browse'}
        backgroundColor={isAlt ? 'transparent' : 'backdrop'}
        hasBorderBottom="desktop"
        hideNav={isAlt ? 'never' : 'mobile'}
      />
      {isAlt ? (
        <MakeModelLanding
          isVans={isVans}
          vehicleModelContent={pageContent}
          range={range}
          faqList={faqList}
          supplierCount={supplierCount}
        />
      ) : (
        <>
          {showPageContent && (
            <SearchContentHeader
              isVans={isVans}
              pageContent={pageContent}
            />
          )}
          <SearchResultsAndFilters
            isVans={isVans}
            setStatus={setStatus}
            status={status}
          />
        </>
      )}
    </>
  )
}

export default CarRangePage

export const getServerSideProps = async context => {
  const { manufacturer, range } = context.params
  const isAlt = range.endsWith('-alt')
  const cleanRange = range.replace('-alt', '')

  const manufacturerMatch = FILTERS.manufacturers.find(m => m.name.toLowerCase() === manufacturer)
  const manufacturerId = manufacturerMatch?.id
  if (!manufacturerId && isAlt) {
    return {
      notFound: true,
    }
  }

  let rangeMatch = null
  try {
    if (isAlt) {
      const ranges = await getRangesForManufacturersService({
        ...INITIAL_STATE.searchParameters,
        manufacturers: [manufacturerId],
      })

      rangeMatch = ranges.find(v => v.name.toLowerCase() === cleanRange)

      if (!rangeMatch) {
        return {
          notFound: true,
        }
      }
    }
  } catch (error) {
    rangeMatch = null
  }

  try {
    let pageContent = null
    let faqList = null
    let supplierCount = null

    try {
      const contentResponse = await getPageContentItemService({
        type: isAlt ? 'ENHANCED_VEHICLE_MODEL' : 'BANNER',
        page: '1',
        urlKey: `car-leasing/${context.params.manufacturer}/${context.params.range}`,
      })

      pageContent = {
        ...contentResponse.content,
        urlKey: contentResponse.urlKey,
      }
    } catch (error) {
      return {
        notFound: true,
      }
    }

    try {
      const faqResponse = await getPageContentItemService({
        type: 'FAQ_SEARCH',
        urlKey: `car-leasing/${context.params.manufacturer}/${context.params.range}`,
      })

      faqList = Array.isArray(faqResponse?.content?.items)
        ? faqResponse.content.items.filter(
            faq => Number(faq.vehicleType) === FILTER_VEHICLE_TYPES_CARS,
          )
        : []
    } catch (error) {
      faqList = null
    }

    try {
      if (isAlt) {
        const response = await getListOfAllSuppliersService()

        supplierCount = response.length
      }
    } catch (e) {
      supplierCount = null
    }

    return {
      props: {
        isAlt,
        pageContent,
        range: rangeMatch,
        faqList,
        supplierCount,
      },
    }
  } catch {
    if (isAlt) {
      return {
        redirect: {
          destination: `/car-leasing/${manufacturer}/${cleanRange}`,
          permanent: false,
        },
      }
    }

    return {
      notFound: true,
    }
  }
}
